// src/components/RhinoVOIPReports.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  Icon,
  UtilityInlineGrid,
} from 'rhinostyle';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useGetCdrsQuery, useGetMembersWithExtensionsQuery, useGetRingGroupsQuery } from '../services/VoipService';
import PageLoader from './PageLoader';
import { formatStatus } from '../helpers/VoipHelpers';

const PAGE_SIZE = 1000;

const DATE_PICKER_LABELS = [
  { id: 1, label: 'Yesterday', duration: 1 },
  { id: 2, label: 'Last 7 Days', duration: 6 },
  { id: 3, label: 'Last 30 Days', duration: 29 },
  { id: 4, label: 'Last 90 Days', duration: 89 },
  { id: 5, label: 'Last 12 Months', duration: 'year' },
  { id: 6, label: 'Custom Date' },
];

const PIE_COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4'];

// Every color on the page is sampled from a single spectral rainbow so
// direction, status, and categorical roles share the same saturation
// and brightness. Pinning by name keeps each role's color stable.
const DIRECTION_COLORS = {
  Inbound: '#3D9BC9', // blue (primary cool)
  Outbound: '#F4C430', // golden yellow (warm, complement to Inbound blue)
};
const STATUS_COLORS = {
  Answered: '#5DBE85', // deeper mint — positive
  Voicemail: '#F37846', // orange — voicemail convention
  Missed: '#A60B4F', // deep magenta — strongest negative
  'No Answer': '#FFC274', // light gold — warning warm
  Busy: '#4FBFA8', // teal
  Failed: '#E94B4F', // coral — error variant, distinct from Missed
};
const STATUS_FALLBACK_COLORS = ['#FB9C57', '#B0E78C', '#FFD78F', '#DCEC8A'];
const colorForStatus = (name, idx) =>
  STATUS_COLORS[name] || STATUS_FALLBACK_COLORS[idx % STATUS_FALLBACK_COLORS.length];

const REPORT_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'members', label: 'Members' },
  { id: 'ringGroups', label: 'Ring Groups' },
];

// Categorical palette for per-member / per-ring-group charts. Uses the
// Tableau 10 scheme — a separate, vivid, well-known palette designed
// specifically for distinguishing many categories at once. Kept distinct
// from the spectral direction/status palette so a member's color never
// reads as "Inbound" or "Answered." Tableau 10 sits at similar brightness
// to the rainbow so the page feels cohesive.
const MEMBER_CHART_COLORS = [
  '#4E79A7', // steel blue
  '#F28E2C', // warm orange
  '#E15759', // brick red
  '#76B7B2', // muted teal
  '#59A14F', // grass green
  '#EDC949', // ochre
  '#AF7AA1', // dusty purple
  '#FF9DA7', // salmon pink
  '#9C755F', // tan brown
  '#BAB0AB', // warm grey
];

const TZ_STORAGE_KEY = 'voipReportsTimezone';

const TIMEZONE_OPTIONS = [
  { id: 'auto', label: 'My Current Timezone' },
  { id: 'America/New_York', label: 'Eastern Time' },
  { id: 'America/Chicago', label: 'Central Time' },
  { id: 'America/Denver', label: 'Mountain Time' },
  { id: 'America/Phoenix', label: 'Mountain Time - Arizona' },
  { id: 'America/Los_Angeles', label: 'Pacific Time' },
  { id: 'America/Anchorage', label: 'Alaska Time' },
  { id: 'Pacific/Honolulu', label: 'Hawaii Time' },
];

const readStoredTz = () => {
  try {
    return localStorage.getItem(TZ_STORAGE_KEY) || 'auto';
  } catch {
    return 'auto';
  }
};

function downloadCsv(columns, rows, filename) {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const header = columns.map((c) => escape(c.header)).join(',');
  const body = rows.map((row) => columns.map((c) => escape(typeof c.format === 'function' ? c.format(row) : row[c.key])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const RhinoVOIPReports = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(3);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRingGroup, setSelectedRingGroup] = useState(null);
  const [sortColumn, setSortColumn] = useState('total');
  const [sortAsc, setSortAsc] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());
  const [hiddenDirections, setHiddenDirections] = useState(new Set());
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [hiddenLineMembers, setHiddenLineMembers] = useState(new Set());
  const [summaryGrouping, setSummaryGrouping] = useState('total');
  const [summarySortColumn, setSummarySortColumn] = useState('period');
  const [summarySortAsc, setSummarySortAsc] = useState(true);
  const [memberGrouping, setMemberGrouping] = useState('total');
  const [rgGrouping, setRgGrouping] = useState('total');
  const [rgSortColumn, setRgSortColumn] = useState('total');
  const [rgSortAsc, setRgSortAsc] = useState(false);
  const [hiddenRingGroups, setHiddenRingGroups] = useState(new Set());
  const [hiddenLineRingGroups, setHiddenLineRingGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState('graphs');
  const [tzPref, setTzPref] = useState(readStoredTz);

  useEffect(() => {
    try { localStorage.setItem(TZ_STORAGE_KEY, tzPref); } catch { /* private mode / disabled */ }
  }, [tzPref]);

  // 'auto' resolves to the browser's IANA zone every render, so the user's
  // device timezone tracks them if it changes (e.g. travel + VPN).
  const tz = useMemo(() => (tzPref === 'auto' ? moment.tz.guess() : tzPref), [tzPref]);

  const tzLabel = useMemo(() => {
    const opt = TIMEZONE_OPTIONS.find((o) => o.id === tzPref);
    if (!opt) return 'My Current Timezone';
    return tzPref === 'auto' ? `My Current Timezone (${tz})` : opt.label;
  }, [tzPref, tz]);

  // The picker returns moments in browser-local. Reinterpret the picked
  // calendar dates in the selected tz so "Apr 1" always means Apr 1 in
  // the chosen zone — regardless of where the viewer's browser is.
  const pickedStart = useMemo(() =>
    moment.tz(startDate.format('YYYY-MM-DD'), tz).startOf('day'),
  [startDate, tz]);
  const pickedEnd = useMemo(() =>
    moment.tz(endDate.format('YYYY-MM-DD'), tz).startOf('day'),
  [endDate, tz]);

  const { data: membersData } = useGetMembersWithExtensionsQuery();
  const members = Array.isArray(membersData?.objects) ? membersData.objects : [];

  const { data: ringGroupsData } = useGetRingGroupsQuery();
  const allRingGroups = Array.isArray(ringGroupsData?.objects) ? ringGroupsData.objects : [];

  const filters = useMemo(() => ({
    // Send the picked window as UTC instants in the selected tz so the
    // backend's UTC-stored stamps are compared correctly. Date-only
    // strings would land on UTC midnight and miss late-evening calls
    // that fall on the next UTC day.
    start_stamp: pickedStart.toISOString(),
    end_stamp: pickedEnd.clone().endOf('day').toISOString(),
    limit: PAGE_SIZE,
    offset: 0,
  }), [pickedStart, pickedEnd]);

  const { data, isLoading, isFetching } = useGetCdrsQuery(filters);
  const allRecords = Array.isArray(data?.objects) ? data.objects : [];
  const records = useMemo(() => {
    // BUG-068/069: enforce the selected window client-side. The backend's
    // date filter is timezone-boundary fuzzy (a 'YYYY-MM-DD' window can be
    // interpreted as UTC server-side while the user picks local days), so
    // without this guard a Mar 31 11pm local call leaks into an Apr 1-2
    // selection and shows up as a "03/31" bucket in the table.
    const rangeStart = pickedStart.valueOf();
    const rangeEnd = pickedEnd.clone().endOf('day').valueOf();
    let filtered = allRecords.filter((r) => {
      if (!r.start_stamp) return false;
      // Exclude internal (extension-to-extension) calls. A single internal
      // call lands on both the caller and the answerer in member breakdowns,
      // double-counting and skewing per-member totals/averages. Reports are
      // about external call activity — internal traffic isn't relevant here.
      if ((r.direction || '').toLowerCase() === 'local') return false;
      const t = new Date(r.start_stamp).getTime();
      return Number.isFinite(t) && t >= rangeStart && t <= rangeEnd;
    });
    if (selectedMember) {
      // BUG-067: match on the extension fields exactly, not caller_id_number substring
      filtered = filtered.filter((r) =>
        String(r.caller_extension || '') === String(selectedMember)
        || String(r.dest_extension || '') === String(selectedMember));
    }
    if (selectedRingGroup) {
      filtered = filtered.filter((r) => r.ring_group_name === selectedRingGroup);
    }
    return filtered;
  }, [allRecords, selectedMember, selectedRingGroup, pickedStart, pickedEnd]);

  const memberLabel = useMemo(() => {
    if (!selectedMember) return 'All Members';
    const m = members.find((mb) => mb.extension === selectedMember);
    return m ? `${m.firstName} ${m.lastName}` : selectedMember;
  }, [selectedMember, members]);

  // BUG-065: populate the dropdown from the full ring groups list so groups with
  // no calls in the selected range still appear.
  const ringGroupOptions = useMemo(() => {
    const seen = new Set();
    const fromConfig = allRingGroups.map((rg) => {
      const name = rg.ring_group_name;
      seen.add(name);
      return {
        id: name,
        value: rg.ring_group_extension ? `${name} (${rg.ring_group_extension})` : name,
      };
    });
    // Include any ring group that shows up in records but isn't in the config list (fallback)
    allRecords.forEach((r) => {
      const name = r.ring_group_name;
      if (name && !seen.has(name)) {
        seen.add(name);
        fromConfig.push({ id: name, value: name });
      }
    });
    return fromConfig.sort((a, b) => a.value.localeCompare(b.value));
  }, [allRingGroups, allRecords]);

  const ringGroupLabel = selectedRingGroup || 'All Ring Groups';

  const isAnyFilterSelected = !!selectedMember || !!selectedRingGroup;

  const clearAllFilters = () => {
    setSelectedMember(null);
    setSelectedRingGroup(null);
  };

  // --- Date handlers ---

  const MAX_RANGE_MONTHS = 12;

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      const start = moment(date.startDate);
      const end = moment(date.endDate);
      if (end.diff(start, 'months', true) > MAX_RANGE_MONTHS) {
        start.set(end.clone().subtract(MAX_RANGE_MONTHS, 'months').toObject());
      }
      setStartDate(start);
      setEndDate(end);
      setActiveKey(date.activeKey);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();
    if (selectedLabelIndex === 1) {
      toDate = moment().subtract(1, 'days');
    }
    if (duration === 'year') {
      fromDate = moment().subtract(1, 'years').add(1, 'days');
    }
    return { startDate: fromDate, endDate: toDate };
  };

  // --- Chart data computations ---

  // All bucket labels (day / week / month) use the viewer's local timezone so
  // they agree with each other and with the user's sense of "what day did this
  // call happen on." Mixing UTC week/month with local day previously produced
  // boundary records that fell into different buckets across charts.
  function localDay(stamp) {
    return moment.tz(stamp, tz).format('MM/DD');
  }

  const totalDaysInRange = useMemo(() =>
    Math.max(pickedEnd.diff(pickedStart, 'days') + 1, 1),
  [pickedStart, pickedEnd]);

  const callsByHour = useMemo(() => {
    const inboundByHour = Array(24).fill(0);
    const outboundByHour = Array(24).fill(0);

    records.forEach((r) => {
      if (!r.start_stamp) return;
      // BUG-066: bucket by selected-tz hour
      const h = moment.tz(r.start_stamp, tz).hour();
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') inboundByHour[h] += 1;
      else if (dir === 'outbound') outboundByHour[h] += 1;
    });

    return inboundByHour.map((_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      inbound: Math.round((inboundByHour[hour] / totalDaysInRange) * 10) / 10,
      outbound: Math.round((outboundByHour[hour] / totalDaysInRange) * 10) / 10,
    }));
  }, [records, totalDaysInRange, tz]);

  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const avgCallsByDayOfWeek = useMemo(() => {
    // Count how many of each weekday exist in the selected date range
    const dowCountsInRange = Array(7).fill(0);
    const cursor = pickedStart.clone();
    const end = pickedEnd.clone();
    while (cursor.isSameOrBefore(end)) {
      // moment day(): 0=Sun, convert to 0=Mon
      const dow = (cursor.day() + 6) % 7;
      dowCountsInRange[dow] += 1;
      cursor.add(1, 'day');
    }

    const inboundByDow = Array(7).fill(0);
    const outboundByDow = Array(7).fill(0);
    records.forEach((r) => {
      if (!r.start_stamp) return;
      // BUG-064: classify by selected-tz weekday so a late-Friday call doesn't count as Saturday
      const dow = (moment.tz(r.start_stamp, tz).day() + 6) % 7;
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') inboundByDow[dow] += 1;
      else if (dir === 'outbound') outboundByDow[dow] += 1;
    });

    return DAY_NAMES.map((name, i) => {
      const days = dowCountsInRange[i];
      return {
        day: name,
        inbound: days > 0 ? Math.round((inboundByDow[i] / days) * 10) / 10 : 0,
        outbound: days > 0 ? Math.round((outboundByDow[i] / days) * 10) / 10 : 0,
      };
    });
  }, [records, pickedStart, pickedEnd, tz]);

  // BUG-069: seed every day in the selected range so empty days still appear on the chart
  const daysInRange = useMemo(() => {
    const days = [];
    const cursor = pickedStart.clone();
    const end = pickedEnd.clone();
    while (cursor.isSameOrBefore(end)) {
      days.push(cursor.format('MM/DD'));
      cursor.add(1, 'day');
    }
    return days;
  }, [pickedStart, pickedEnd]);

  const callsByDirectionPerDay = useMemo(() => {
    const dayCounts = {};
    daysInRange.forEach((d) => { dayCounts[d] = { inbound: 0, outbound: 0 }; });

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const day = localDay(r.start_stamp);
      if (!dayCounts[day]) dayCounts[day] = { inbound: 0, outbound: 0 };
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') dayCounts[day].inbound += 1;
      else if (dir === 'outbound') dayCounts[day].outbound += 1;
    });

    return daysInRange.map((date) => ({ date, ...dayCounts[date] }))
      .sort((a, b) => {
        const [am, ad] = a.date.split('/').map(Number);
        const [bm, bd] = b.date.split('/').map(Number);
        return am - bm || ad - bd;
      });
  }, [records, daysInRange]);

  // Inbound only — outbound calls don't have a meaningful "missed/answered/voicemail"
  // breakdown from the practice's perspective, and mixing them in obscures the
  // inbound funnel that the chart is actually useful for.
  const callsByStatus = useMemo(() => {
    const counts = {};
    records.forEach((r) => {
      if ((r.direction || '').toLowerCase() !== 'inbound') return;
      const status = r.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: formatStatus(name), value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  const callsByDirection = useMemo(() => {
    const counts = {};
    records.forEach((r) => {
      const dir = r.direction || 'unknown';
      counts[dir] = (counts[dir] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  // --- Summary table data ---

  function localWeek(stamp) {
    const d = moment.tz(stamp, tz);
    return `${d.clone().startOf('isoWeek').format('MM/DD')} - ${d.clone().endOf('isoWeek').format('MM/DD')}`;
  }

  function localMonth(stamp) {
    return moment.tz(stamp, tz).format('MM/YYYY');
  }

  const summaryTableData = useMemo(() => {
    const buckets = {};

    const getBucketKey = (r) => {
      if (summaryGrouping === 'total') return 'Total';
      if (summaryGrouping === 'day') return localDay(r.start_stamp);
      if (summaryGrouping === 'week') return localWeek(r.start_stamp);
      if (summaryGrouping === 'month') return localMonth(r.start_stamp);
      return 'Total';
    };

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const key = getBucketKey(r);
      if (!buckets[key]) {
        buckets[key] = { total: 0, inbound: 0, outbound: 0, missed: 0, voicemail: 0, failed: 0, totalDuration: 0, answeredCount: 0 };
      }
      const b = buckets[key];
      b.total += 1;
      const dir = (r.direction || '').toLowerCase();
      if (dir === 'inbound') b.inbound += 1;
      else if (dir === 'outbound') b.outbound += 1;
      // Voicemail rolls into missed: a voicemail IS a missed call where the
      // caller chose to leave a message. The voicemail column counts the
      // subset that left a message; this missed count includes them.
      const isMissed = r.status === 'missed' || r.status === 'no_answer' || r.status === 'voicemail';
      if (isMissed) b.missed += 1;
      if (r.status === 'voicemail') b.voicemail += 1;
      if (r.status === 'failed' || r.status === 'busy') b.failed += 1;
      const duration = r.billsec || r.duration || 0;
      if (!isMissed && duration > 0) {
        b.totalDuration += duration;
        b.answeredCount += 1;
      }
    });

    // BUG-063: For week/month buckets, count only the days inside the selected date range.
    // A partial week at the edge of the range shouldn't be divided by 7.
    const clamp = (bucketStart, bucketEnd) => {
      const s = moment.max(bucketStart, pickedStart);
      const e = moment.min(bucketEnd, pickedEnd);
      return Math.max(e.diff(s, 'days') + 1, 1);
    };
    const daysPerBucket = (key) => {
      if (summaryGrouping === 'total') return totalDaysInRange;
      if (summaryGrouping === 'day') return 1;
      if (summaryGrouping === 'week') {
        const [startMd] = key.split(' - ');
        const [mm, dd] = startMd.split('/').map(Number);
        const year = pickedStart.year();
        const bucketStart = moment.tz({ year, month: mm - 1, day: dd }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().add(6, 'days');
        return clamp(bucketStart, bucketEnd);
      }
      if (summaryGrouping === 'month') {
        const [mm, yyyy] = key.split('/').map(Number);
        const bucketStart = moment.tz({ year: yyyy, month: mm - 1, day: 1 }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().endOf('month').startOf('day');
        return clamp(bucketStart, bucketEnd);
      }
      return 1;
    };

    return Object.entries(buckets).map(([period, b]) => ({
      period,
      total: b.total,
      avgPerDay: Math.round((b.total / daysPerBucket(period)) * 10) / 10,
      inbound: b.inbound,
      outbound: b.outbound,
      missed: b.missed,
      voicemail: b.voicemail,
      failed: b.failed,
      // % Missed is missed / inbound — outbound calls can't be "missed,"
      // so including them in the denominator under-states the rate.
      pctMissed: b.inbound > 0 ? Math.round((b.missed / b.inbound) * 100) : 0,
      avgHandleTime: b.answeredCount > 0 ? Math.round(b.totalDuration / b.answeredCount) : 0,
    }));
  }, [records, summaryGrouping, totalDaysInRange, pickedStart, pickedEnd, tz]);

  const handleSummarySort = (col) => {
    if (summarySortColumn === col) {
      setSummarySortAsc((prev) => !prev);
    } else {
      setSummarySortColumn(col);
      setSummarySortAsc(col === 'period');
    }
  };

  const sortedSummaryTableData = useMemo(() => {
    const sorted = [...summaryTableData].sort((a, b) => {
      const aVal = a[summarySortColumn];
      const bVal = b[summarySortColumn];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal);
      return aVal - bVal;
    });
    return summarySortAsc ? sorted : sorted.reverse();
  }, [summaryTableData, summarySortColumn, summarySortAsc]);

  // --- Members tab data ---

  const memberStats = useMemo(() => {
    // Seed with all known members — but if a single member is filtered,
    // only seed that one so the table shows just their row instead of
    // every member with zeros.
    const stats = {};
    const seedMembers = selectedMember
      ? members.filter((m) => String(m.extension) === String(selectedMember))
      : members;
    seedMembers.forEach((m) => {
      stats[m.extension] = {
        extension: m.extension,
        name: `${m.firstName} ${m.lastName}`,
        total: 0,
        inbound: 0,
        outbound: 0,
        missed: 0,
        voicemail: 0,
        failed: 0,
        totalDuration: 0,
        answeredCount: 0,
      };
    });

    records.forEach((r) => {
      const callerExt = r.caller_extension;
      const callerName = r.caller_extension_name || r.caller_id_name;
      const destExt = r.dest_extension;
      const destName = r.dest_name;
      const dir = (r.direction || '').toLowerCase();
      // Voicemail rolls into missed: a voicemail IS a missed call where the
      // caller chose to leave a message. The voicemail column counts the
      // subset that left a message; this missed count includes them.
      const isMissed = r.status === 'missed' || r.status === 'no_answer' || r.status === 'voicemail';
      const isVoicemail = r.status === 'voicemail';
      const isFailed = r.status === 'failed' || r.status === 'busy';
      const duration = r.billsec || r.duration || 0;

      const touch = (ext, name) => {
        if (!ext) return;
        if (!stats[ext]) {
          stats[ext] = { extension: ext, name: name || ext, total: 0, inbound: 0, outbound: 0, missed: 0, voicemail: 0, failed: 0, totalDuration: 0, answeredCount: 0 };
        }
        stats[ext].total += 1;
        if (dir === 'inbound') stats[ext].inbound += 1;
        else if (dir === 'outbound') stats[ext].outbound += 1;
        if (isMissed) stats[ext].missed += 1;
        if (isVoicemail) stats[ext].voicemail += 1;
        if (isFailed) stats[ext].failed += 1;
        if (!isMissed && duration > 0) {
          stats[ext].totalDuration += duration;
          stats[ext].answeredCount += 1;
        }
      };

      // BUG-068: attribute each call to exactly one member so sum(member totals) == summary total.
      // Outbound/local → the caller; Inbound → the dest (answerer, incl. ring group pickups).
      if (dir === 'inbound') {
        if (destExt) touch(destExt, destName);
        else if (callerExt) touch(callerExt, callerName);
      } else if (callerExt) {
        touch(callerExt, callerName);
      } else if (destExt) {
        touch(destExt, destName);
      }
    });

    return Object.values(stats)
      .map((m) => ({
        name: m.name,
        extension: m.extension,
        total: m.total,
        avgPerDay: Math.round((m.total / totalDaysInRange) * 10) / 10,
        inbound: m.inbound,
        outbound: m.outbound,
        missed: m.missed,
        voicemail: m.voicemail,
        failed: m.failed,
        pctMissed: m.inbound > 0 ? Math.round((m.missed / m.inbound) * 100) : 0,
        avgHandleTime: m.answeredCount > 0 ? Math.round(m.totalDuration / m.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, members, totalDaysInRange, selectedMember]);

  // Keep a chart-friendly alias
  const callsPerMember = memberStats;

  const memberTotalsPieData = useMemo(() =>
    memberStats.filter((m) => m.total > 0).map((m) => ({ name: m.name, value: m.total })),
  [memberStats]);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortAsc((prev) => !prev);
    } else {
      setSortColumn(col);
      setSortAsc(col === 'name' || col === 'extension');
    }
  };

  const memberStatsByPeriod = useMemo(() => {
    if (memberGrouping === 'total') return memberStats;

    const buckets = {};
    const getBucketKey = (r) => {
      if (memberGrouping === 'day') return localDay(r.start_stamp);
      if (memberGrouping === 'week') return localWeek(r.start_stamp);
      if (memberGrouping === 'month') return localMonth(r.start_stamp);
      return 'Total';
    };

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const period = getBucketKey(r);

      const callerExt = r.caller_extension;
      const callerName = r.caller_extension_name || r.caller_id_name;
      const destExt = r.dest_extension;
      const destName = r.dest_name;
      const dir = (r.direction || '').toLowerCase();
      // Voicemail rolls into missed: a voicemail IS a missed call where the
      // caller chose to leave a message. The voicemail column counts the
      // subset that left a message; this missed count includes them.
      const isMissed = r.status === 'missed' || r.status === 'no_answer' || r.status === 'voicemail';
      const isVoicemail = r.status === 'voicemail';
      const isFailed = r.status === 'failed' || r.status === 'busy';
      const duration = r.billsec || r.duration || 0;

      const touch = (ext, name) => {
        if (!ext) return;
        const key = `${ext}|${period}`;
        if (!buckets[key]) {
          buckets[key] = {
            extension: ext,
            name: name || ext,
            period,
            total: 0,
            inbound: 0,
            outbound: 0,
            missed: 0,
            voicemail: 0,
            failed: 0,
            totalDuration: 0,
            answeredCount: 0,
          };
        }
        buckets[key].total += 1;
        if (dir === 'inbound') buckets[key].inbound += 1;
        else if (dir === 'outbound') buckets[key].outbound += 1;
        if (isMissed) buckets[key].missed += 1;
        if (isVoicemail) buckets[key].voicemail += 1;
        if (isFailed) buckets[key].failed += 1;
        if (!isMissed && duration > 0) {
          buckets[key].totalDuration += duration;
          buckets[key].answeredCount += 1;
        }
      };

      // BUG-068: attribute each call to one member (see note in memberStats)
      if (dir === 'inbound') {
        if (destExt) touch(destExt, destName);
        else if (callerExt) touch(callerExt, callerName);
      } else if (callerExt) {
        touch(callerExt, callerName);
      } else if (destExt) {
        touch(destExt, destName);
      }
    });

    // BUG-063: clamp bucket days to selected range, same as summary
    const clamp = (bucketStart, bucketEnd) => {
      const s = moment.max(bucketStart, pickedStart);
      const e = moment.min(bucketEnd, pickedEnd);
      return Math.max(e.diff(s, 'days') + 1, 1);
    };
    const daysPerBucket = (period) => {
      if (memberGrouping === 'day') return 1;
      if (memberGrouping === 'week') {
        const [startMd] = period.split(' - ');
        const [mm, dd] = startMd.split('/').map(Number);
        const year = pickedStart.year();
        const bucketStart = moment.tz({ year, month: mm - 1, day: dd }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().add(6, 'days');
        return clamp(bucketStart, bucketEnd);
      }
      if (memberGrouping === 'month') {
        const [mm, yyyy] = period.split('/').map(Number);
        const bucketStart = moment.tz({ year: yyyy, month: mm - 1, day: 1 }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().endOf('month').startOf('day');
        return clamp(bucketStart, bucketEnd);
      }
      return 1;
    };

    // Seed all members for each period so they appear even with 0 calls.
    // When a single member is filtered, only seed that one so the table
    // doesn't fill with zero rows for everyone else.
    const periods = [...new Set(Object.values(buckets).map((b) => b.period))];
    const seedMembers = selectedMember
      ? members.filter((m) => String(m.extension) === String(selectedMember))
      : members;
    seedMembers.forEach((m) => {
      periods.forEach((period) => {
        const key = `${m.extension}|${period}`;
        if (!buckets[key]) {
          buckets[key] = {
            extension: m.extension,
            name: `${m.firstName} ${m.lastName}`,
            period,
            total: 0,
            inbound: 0,
            outbound: 0,
            missed: 0,
            voicemail: 0,
            failed: 0,
            totalDuration: 0,
            answeredCount: 0,
          };
        }
      });
    });

    return Object.values(buckets)
      .map((b) => ({
        name: b.name,
        extension: b.extension,
        period: b.period,
        total: b.total,
        avgPerDay: Math.round((b.total / daysPerBucket(b.period)) * 10) / 10,
        inbound: b.inbound,
        outbound: b.outbound,
        missed: b.missed,
        voicemail: b.voicemail,
        failed: b.failed,
        pctMissed: b.inbound > 0 ? Math.round((b.missed / b.inbound) * 100) : 0,
        avgHandleTime: b.answeredCount > 0 ? Math.round(b.totalDuration / b.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, members, memberStats, memberGrouping, pickedStart, pickedEnd, tz, selectedMember]);

  const sortedMemberStats = useMemo(() => {
    const sorted = [...memberStatsByPeriod].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal);
      return aVal - bVal;
    });
    return sortAsc ? sorted : sorted.reverse();
  }, [memberStatsByPeriod, sortColumn, sortAsc]);

  const memberCallsPerDay = useMemo(() => {
    // Seed every day in the selected range with empty counts so the line
    // chart renders a continuous x-axis. Without this, recharts collapses
    // the gaps and draws straight lines between non-adjacent days, making
    // it look like activity flowed across days that actually had zero.
    const dayMap = {};
    daysInRange.forEach((d) => { dayMap[d] = {}; });
    const memberKeys = new Set();

    records.forEach((r) => {
      if (!r.start_stamp) return;
      const day = localDay(r.start_stamp);
      if (!dayMap[day]) dayMap[day] = {};

      const callerExt = r.caller_extension;
      const destExt = r.dest_extension;

      if (callerExt) {
        const key = callerExt;
        memberKeys.add(key);
        dayMap[day][key] = (dayMap[day][key] || 0) + 1;
      }
      if (destExt && destExt !== callerExt) {
        const key = destExt;
        memberKeys.add(key);
        dayMap[day][key] = (dayMap[day][key] || 0) + 1;
      }
    });

    // Backfill 0 for every member on every seeded day so each line draws
    // through the full window even when the member had no activity that day.
    const memberKeyList = [...memberKeys];
    return {
      data: daysInRange.map((date) => {
        const counts = dayMap[date] || {};
        const filled = { date };
        memberKeyList.forEach((k) => { filled[k] = counts[k] || 0; });
        return filled;
      }),
      memberKeys: memberKeyList,
    };
  }, [records, daysInRange, tz]);

  // --- Ring Groups tab data ---

  const ringGroupStats = useMemo(() => {
    const stats = {};
    // Seed with all known ring groups so a group with 0 calls in range still
    // appears — but if a single ring group is filtered, only seed that one.
    const seedRingGroups = selectedRingGroup
      ? allRingGroups.filter((rg) => rg.ring_group_name === selectedRingGroup)
      : allRingGroups;
    seedRingGroups.forEach((rg) => {
      const name = rg.ring_group_name;
      if (!name) return;
      stats[name] = {
        name,
        extension: rg.ring_group_extension || '',
        total: 0,
        answered: 0,
        missed: 0,
        voicemail: 0,
        failed: 0,
        totalDuration: 0,
        answeredCount: 0,
      };
    });

    records.forEach((r) => {
      const name = r.ring_group_name;
      if (!name) return;
      // Only count inbound legs. Outbound rows tagged with a ring_group_uuid
      // are the system→extension bridge legs of the inbound RG call — counting
      // them would double the totals and never match Summary's inbound count.
      if ((r.direction || '').toLowerCase() !== 'inbound') return;
      if (!stats[name]) {
        stats[name] = { name, extension: '', total: 0, answered: 0, missed: 0, voicemail: 0, failed: 0, totalDuration: 0, answeredCount: 0 };
      }
      const s = stats[name];
      s.total += 1;
      // Voicemail rolls into missed: a voicemail IS a missed call where the
      // caller chose to leave a message. The voicemail column counts the
      // subset that left a message; this missed count includes them.
      const isMissed = r.status === 'missed' || r.status === 'no_answer' || r.status === 'voicemail';
      if (isMissed) s.missed += 1;
      else if (r.status === 'answered') s.answered += 1;
      if (r.status === 'voicemail') s.voicemail += 1;
      if (r.status === 'failed' || r.status === 'busy') s.failed += 1;
      const duration = r.billsec || r.duration || 0;
      if (!isMissed && duration > 0) {
        s.totalDuration += duration;
        s.answeredCount += 1;
      }
    });

    return Object.values(stats)
      .map((s) => ({
        name: s.name,
        extension: s.extension,
        total: s.total,
        avgPerDay: Math.round((s.total / totalDaysInRange) * 10) / 10,
        answered: s.answered,
        missed: s.missed,
        voicemail: s.voicemail,
        failed: s.failed,
        pctMissed: s.total > 0 ? Math.round((s.missed / s.total) * 100) : 0,
        avgHandleTime: s.answeredCount > 0 ? Math.round(s.totalDuration / s.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, allRingGroups, totalDaysInRange, selectedRingGroup]);

  const callsPerRingGroup = ringGroupStats;

  const ringGroupTotalsPieData = useMemo(() =>
    ringGroupStats.filter((g) => g.total > 0).map((g) => ({ name: g.name, value: g.total })),
  [ringGroupStats]);

  const ringGroupStatsByPeriod = useMemo(() => {
    if (rgGrouping === 'total') return ringGroupStats;

    const buckets = {};
    const getBucketKey = (r) => {
      if (rgGrouping === 'day') return localDay(r.start_stamp);
      if (rgGrouping === 'week') return localWeek(r.start_stamp);
      if (rgGrouping === 'month') return localMonth(r.start_stamp);
      return 'Total';
    };

    records.forEach((r) => {
      if (!r.start_stamp || !r.ring_group_name) return;
      // Inbound only — same reasoning as ringGroupStats above.
      if ((r.direction || '').toLowerCase() !== 'inbound') return;
      const period = getBucketKey(r);
      const key = `${r.ring_group_name}|${period}`;
      if (!buckets[key]) {
        buckets[key] = { name: r.ring_group_name, extension: '', period, total: 0, answered: 0, missed: 0, voicemail: 0, failed: 0, totalDuration: 0, answeredCount: 0 };
      }
      const b = buckets[key];
      b.total += 1;
      // Voicemail rolls into missed: a voicemail IS a missed call where the
      // caller chose to leave a message. The voicemail column counts the
      // subset that left a message; this missed count includes them.
      const isMissed = r.status === 'missed' || r.status === 'no_answer' || r.status === 'voicemail';
      if (isMissed) b.missed += 1;
      else if (r.status === 'answered') b.answered += 1;
      if (r.status === 'voicemail') b.voicemail += 1;
      if (r.status === 'failed' || r.status === 'busy') b.failed += 1;
      const duration = r.billsec || r.duration || 0;
      if (!isMissed && duration > 0) {
        b.totalDuration += duration;
        b.answeredCount += 1;
      }
    });

    // Same partial-bucket clamp as members/summary
    const clamp = (bucketStart, bucketEnd) => {
      const s = moment.max(bucketStart, pickedStart);
      const e = moment.min(bucketEnd, pickedEnd);
      return Math.max(e.diff(s, 'days') + 1, 1);
    };
    const daysPerBucket = (period) => {
      if (rgGrouping === 'day') return 1;
      if (rgGrouping === 'week') {
        const [startMd] = period.split(' - ');
        const [mm, dd] = startMd.split('/').map(Number);
        const year = pickedStart.year();
        const bucketStart = moment.tz({ year, month: mm - 1, day: dd }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().add(6, 'days');
        return clamp(bucketStart, bucketEnd);
      }
      if (rgGrouping === 'month') {
        const [mm, yyyy] = period.split('/').map(Number);
        const bucketStart = moment.tz({ year: yyyy, month: mm - 1, day: 1 }, tz).startOf('day');
        const bucketEnd = bucketStart.clone().endOf('month').startOf('day');
        return clamp(bucketStart, bucketEnd);
      }
      return 1;
    };

    // Seed all known ring groups for each period so a group with 0 calls still
    // appears — but if a single ring group is filtered, only seed that one.
    const periods = [...new Set(Object.values(buckets).map((b) => b.period))];
    const seedRingGroups = selectedRingGroup
      ? allRingGroups.filter((rg) => rg.ring_group_name === selectedRingGroup)
      : allRingGroups;
    seedRingGroups.forEach((rg) => {
      const name = rg.ring_group_name;
      if (!name) return;
      periods.forEach((period) => {
        const key = `${name}|${period}`;
        if (!buckets[key]) {
          buckets[key] = {
            name,
            extension: rg.ring_group_extension || '',
            period,
            total: 0,
            answered: 0,
            missed: 0,
            voicemail: 0,
            failed: 0,
            totalDuration: 0,
            answeredCount: 0,
          };
        }
      });
    });

    return Object.values(buckets)
      .map((b) => ({
        name: b.name,
        extension: b.extension,
        period: b.period,
        total: b.total,
        avgPerDay: Math.round((b.total / daysPerBucket(b.period)) * 10) / 10,
        answered: b.answered,
        missed: b.missed,
        voicemail: b.voicemail,
        failed: b.failed,
        pctMissed: b.total > 0 ? Math.round((b.missed / b.total) * 100) : 0,
        avgHandleTime: b.answeredCount > 0 ? Math.round(b.totalDuration / b.answeredCount) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [records, allRingGroups, ringGroupStats, rgGrouping, pickedStart, pickedEnd, tz, selectedRingGroup]);

  const sortedRingGroupStats = useMemo(() => {
    const sorted = [...ringGroupStatsByPeriod].sort((a, b) => {
      const aVal = a[rgSortColumn];
      const bVal = b[rgSortColumn];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal);
      return aVal - bVal;
    });
    return rgSortAsc ? sorted : sorted.reverse();
  }, [ringGroupStatsByPeriod, rgSortColumn, rgSortAsc]);

  const ringGroupCallsPerDay = useMemo(() => {
    // Seed every day in range so the line chart shows zeros across empty
    // days instead of jumping over them.
    const dayMap = {};
    daysInRange.forEach((d) => { dayMap[d] = {}; });
    const rgKeys = new Set();
    records.forEach((r) => {
      if (!r.start_stamp || !r.ring_group_name) return;
      // Inbound only — bridged outbound legs would double-count.
      if ((r.direction || '').toLowerCase() !== 'inbound') return;
      const day = localDay(r.start_stamp);
      if (!dayMap[day]) dayMap[day] = {};
      const key = r.ring_group_name;
      rgKeys.add(key);
      dayMap[day][key] = (dayMap[day][key] || 0) + 1;
    });
    const rgKeyList = [...rgKeys];
    return {
      data: daysInRange.map((date) => {
        const counts = dayMap[date] || {};
        const filled = { date };
        rgKeyList.forEach((k) => { filled[k] = counts[k] || 0; });
        return filled;
      }),
      rgKeys: rgKeyList,
    };
  }, [records, daysInRange, tz]);

  const handleRgSort = (col) => {
    if (rgSortColumn === col) {
      setRgSortAsc((prev) => !prev);
    } else {
      setRgSortColumn(col);
      setRgSortAsc(col === 'name' || col === 'extension');
    }
  };

  // --- Helpers ---

  function formatHandleTime(seconds) {
    if (!seconds) return '00:00';
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  }

  const toggleStatus = useCallback((entry) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const toggleDirection = useCallback((entry) => {
    setHiddenDirections((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const filteredCallsByStatus = useMemo(() =>
    callsByStatus.filter((d) => !hiddenStatuses.has(d.name)),
  [callsByStatus, hiddenStatuses]);

  const filteredCallsByDirection = useMemo(() =>
    callsByDirection.filter((d) => !hiddenDirections.has(d.name)),
  [callsByDirection, hiddenDirections]);

  const statusLegendPayload = useMemo(() =>
    callsByStatus.map((d, i) => ({ value: d.name, color: colorForStatus(d.name, i), type: 'circle', inactive: hiddenStatuses.has(d.name) })),
  [callsByStatus, hiddenStatuses]);

  const directionLegendPayload = useMemo(() =>
    callsByDirection.map((d) => ({ value: d.name, color: DIRECTION_COLORS[d.name] || PIE_COLORS[0], type: 'circle', inactive: hiddenDirections.has(d.name) })),
  [callsByDirection, hiddenDirections]);

  const toggleLineMember = useCallback((entry) => {
    setHiddenLineMembers((prev) => {
      const next = new Set(prev);
      if (next.has(entry.dataKey)) next.delete(entry.dataKey);
      else next.add(entry.dataKey);
      return next;
    });
  }, []);

  const toggleMember = useCallback((entry) => {
    setHiddenMembers((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const filteredMemberTotalsPieData = useMemo(() =>
    memberTotalsPieData.filter((d) => !hiddenMembers.has(d.name)),
  [memberTotalsPieData, hiddenMembers]);

  const memberTotalsLegendPayload = useMemo(() =>
    memberTotalsPieData.map((d, i) => ({ value: d.name, color: MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length], type: 'circle', inactive: hiddenMembers.has(d.name) })),
  [memberTotalsPieData, hiddenMembers]);

  const toggleRingGroup = useCallback((entry) => {
    setHiddenRingGroups((prev) => {
      const next = new Set(prev);
      if (next.has(entry.value)) next.delete(entry.value);
      else next.add(entry.value);
      return next;
    });
  }, []);

  const toggleLineRingGroup = useCallback((entry) => {
    setHiddenLineRingGroups((prev) => {
      const next = new Set(prev);
      if (next.has(entry.dataKey)) next.delete(entry.dataKey);
      else next.add(entry.dataKey);
      return next;
    });
  }, []);

  const filteredRingGroupTotalsPieData = useMemo(() =>
    ringGroupTotalsPieData.filter((d) => !hiddenRingGroups.has(d.name)),
  [ringGroupTotalsPieData, hiddenRingGroups]);

  const ringGroupTotalsLegendPayload = useMemo(() =>
    ringGroupTotalsPieData.map((d, i) => ({ value: d.name, color: MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length], type: 'circle', inactive: hiddenRingGroups.has(d.name) })),
  [ringGroupTotalsPieData, hiddenRingGroups]);

  const csvFilename = (title) => `${title}_${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.csv`;

  // --- Render ---

  const hasData = records.length > 0;
  const renderChart = (height, chart) => (hasData ? chart : (
    <div
      className="voip-reports__chart-empty"
      style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}
    >
      No data for this selection
    </div>
  ));

  if (isLoading || isFetching) {
    return (
      <div className="app-page__container">
        <div className="app-panels">
          <div className="list-panel__wrapper">
            <div className="list-panel">
              <PageLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header u-flex-direction-column">
              <div className="app-page__header__title cdr-records__title">VoIP Reports</div>
            </div>
            <div className="box responsive-table cdr-records__grid">
              <div className="cdr-records__filter-wrapper">
                <div className="cdr-records__filter-label">
                  FILTER BY:
                  {isAnyFilterSelected && (
                    <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
                  )}
                </div>
                <div className="row">
                  <div>
                    <UtilityInlineGrid>
                      <DateRangeDropdown
                        name="Reports Date Picker"
                        startDate={startDate}
                        endDate={endDate}
                        selectDate={selectDate}
                        minDate={moment().subtract(1, 'year')}
                        maxDate={moment()}
                        activeKey={activeKey}
                        position="center"
                        dropdownMenuItems={DATE_PICKER_LABELS}
                        selectDateRange={calculateDateRange}
                        dataCypress="reportsDatePicker"
                        isCustomDate
                      />
                      <Dropdown
                        position="bottom"
                        activeKey={selectedMember || ''}
                        label={memberLabel}
                        type={selectedMember ? 'primary' : 'input'}
                        outlined={!!selectedMember}
                        name="reportsMember"
                        onSelect={(ext) => setSelectedMember(ext === 'all' || ext === selectedMember ? null : ext)}
                        dataCypress="reportsMember"
                      >
                        <DropdownMenuItem
                          id="all"
                          label="All Members"
                          icon={!selectedMember ? 'checkmark' : ''}
                        />
                        {members.map((m) => (
                          <DropdownMenuItem
                            key={m.extension}
                            id={m.extension}
                            label={`${m.firstName} ${m.lastName} (ext ${m.extension})`}
                            icon={selectedMember === m.extension ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        position="bottom"
                        activeKey={selectedRingGroup || ''}
                        label={ringGroupLabel}
                        type={selectedRingGroup ? 'primary' : 'input'}
                        outlined={!!selectedRingGroup}
                        name="reportsRingGroup"
                        onSelect={(rg) => setSelectedRingGroup(rg === 'all' || rg === selectedRingGroup ? null : rg)}
                        dataCypress="reportsRingGroup"
                      >
                        <DropdownMenuItem
                          id="all"
                          label="All Ring Groups"
                          icon={!selectedRingGroup ? 'checkmark' : ''}
                        />
                        {ringGroupOptions.map((rg) => (
                          <DropdownMenuItem
                            key={rg.id}
                            id={rg.id}
                            label={rg.value}
                            icon={selectedRingGroup === rg.id ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        position="bottom"
                        activeKey={tzPref}
                        label={tzLabel}
                        type={tzPref !== 'auto' ? 'primary' : 'input'}
                        outlined={tzPref !== 'auto'}
                        name="reportsTimezone"
                        onSelect={(id) => setTzPref(id)}
                        dataCypress="reportsTimezone"
                      >
                        {TIMEZONE_OPTIONS.map((opt) => (
                          <DropdownMenuItem
                            key={opt.id}
                            id={opt.id}
                            label={opt.label}
                            icon={tzPref === opt.id ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                    </UtilityInlineGrid>
                  </div>
                </div>
              </div>

              {/* Report Tabs */}
              <div className="voip-reports__tabs">
                <div className="voip-reports__tabs-left">
                  {REPORT_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`voip-reports__tab${activeTab === tab.id ? ' voip-reports__tab--active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="voip-reports__view-toggle">
                  <button
                    type="button"
                    className={`voip-reports__view-btn${viewMode === 'graphs' ? ' voip-reports__view-btn--active' : ''}`}
                    onClick={() => setViewMode('graphs')}
                  >
                    Graphs
                  </button>
                  <button
                    type="button"
                    className={`voip-reports__view-btn${viewMode === 'table' ? ' voip-reports__view-btn--active' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    Table
                  </button>
                </div>
              </div>

              {activeTab === 'summary' && (
                <div className="voip-reports__charts">
                  {viewMode === 'table' && (
                    <div className="voip-reports__chart-card">
                      <div className="voip-reports__table-header">
                        <h3 className="voip-reports__chart-title">Call Summary</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            className="voip-reports__export-btn"
                            onClick={() => downloadCsv(
                              [
                                { key: 'period', header: 'Period' },
                                { key: 'total', header: 'Total Calls' },
                                { key: 'avgPerDay', header: 'Avg. Calls/Day' },
                                { key: 'inbound', header: '# Inbound' },
                                { key: 'outbound', header: '# Outbound' },
                                { key: 'missed', header: '# Missed' },
                                { key: 'voicemail', header: '# Voicemail' },
                                { key: 'failed', header: '# Failed' },
                                { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` },
                                { key: 'avgHandleTime', header: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
                              ],
                              sortedSummaryTableData,
                              csvFilename('Call Summary'),
                            )}
                          >
                            Export CSV
                          </button>
                          <div className="voip-reports__grouping-selector">
                            {[
                              { id: 'total', label: 'Total' },
                              { id: 'day', label: 'By Day' },
                              { id: 'week', label: 'By Week' },
                              { id: 'month', label: 'By Month' },
                            ].map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                className={`voip-reports__grouping-btn${summaryGrouping === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
                                onClick={() => setSummaryGrouping(opt.id)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="voip-reports__table-container">
                        <table className="voip-reports__table">
                          <thead>
                            <tr>
                              {[
                                { key: 'period', label: 'Period' },
                                { key: 'total', label: 'Total Calls' },
                                { key: 'avgPerDay', label: 'Avg. Calls/Day' },
                                { key: 'inbound', label: '# Inbound' },
                                { key: 'outbound', label: '# Outbound' },
                                { key: 'missed', label: '# Missed' },
                                { key: 'voicemail', label: '# Voicemail' },
                                { key: 'failed', label: '# Failed' },
                                { key: 'pctMissed', label: '% Missed' },
                                { key: 'avgHandleTime', label: 'Avg. Handle Time' },
                              ].map((col) => (
                                <th
                                  key={col.key}
                                  className="voip-reports__th--sortable"
                                  onClick={() => handleSummarySort(col.key)}
                                >
                                  {col.label}
                                  <span className={`voip-reports__sort-arrows${summarySortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                                    <span className={summarySortColumn === col.key && summarySortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                                    <span className={summarySortColumn === col.key && !summarySortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                                  </span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedSummaryTableData.map((row) => (
                              <tr key={row.period}>
                                <td>{row.period}</td>
                                <td>{row.total}</td>
                                <td>{row.avgPerDay}</td>
                                <td>{row.inbound}</td>
                                <td>{row.outbound}</td>
                                <td>{row.missed}</td>
                                <td>{row.voicemail}</td>
                                <td>{row.failed}</td>
                                <td>{row.pctMissed}%</td>
                                <td>{formatHandleTime(row.avgHandleTime)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {viewMode === 'graphs' && (
                    <>
                      <div className="voip-reports__pie-row">
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Inbound Calls by Status</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Status' }, { key: 'value', header: 'Count' }], callsByStatus, csvFilename('Inbound Calls by Status'))}>Export CSV</button>
                          </div>
                          {renderChart(300, (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={filteredCallsByStatus}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  innerRadius={50}
                                  isAnimationActive={false}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                  {filteredCallsByStatus.map((entry) => {
                                    const origIndex = callsByStatus.findIndex((d) => d.name === entry.name);
                                    return <Cell key={entry.name} fill={colorForStatus(entry.name, origIndex)} />;
                                  })}
                                </Pie>
                                <Tooltip />
                                <Legend payload={statusLegendPayload} onClick={toggleStatus} />
                              </PieChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Calls by Direction</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Direction' }, { key: 'value', header: 'Count' }], callsByDirection, csvFilename('Calls by Direction'))}>Export CSV</button>
                          </div>
                          {renderChart(300, (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={filteredCallsByDirection}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  innerRadius={50}
                                  isAnimationActive={false}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                  {filteredCallsByDirection.map((entry) => (
                                    <Cell key={entry.name} fill={DIRECTION_COLORS[entry.name] || PIE_COLORS[0]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend payload={directionLegendPayload} onClick={toggleDirection} />
                              </PieChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls Per Hour</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'hour', header: 'Hour' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], callsByHour, csvFilename('Average Calls Per Hour'))}>Export CSV</button>
                        </div>
                        {renderChart(300, (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={callsByHour}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="hour" tick={{ fontSize: 12 }} interval={1} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="inbound" name="Inbound" fill={DIRECTION_COLORS.Inbound} stackId="hr" />
                              <Bar dataKey="outbound" name="Outbound" fill={DIRECTION_COLORS.Outbound} stackId="hr" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls by Day of Week</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'day', header: 'Day' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], avgCallsByDayOfWeek, csvFilename('Average Calls by Day of Week'))}>Export CSV</button>
                        </div>
                        {renderChart(300, (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={avgCallsByDayOfWeek}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="inbound" name="Inbound" fill={DIRECTION_COLORS.Inbound} stackId="dow" />
                              <Bar dataKey="outbound" name="Outbound" fill={DIRECTION_COLORS.Outbound} stackId="dow" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Incoming vs Outgoing Calls Per Day</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'date', header: 'Date' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], callsByDirectionPerDay, csvFilename('Incoming vs Outgoing Calls Per Day'))}>Export CSV</button>
                        </div>
                        {renderChart(300, (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={callsByDirectionPerDay}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="inbound" name="Inbound" fill={DIRECTION_COLORS.Inbound} stackId="day" />
                              <Bar dataKey="outbound" name="Outbound" fill={DIRECTION_COLORS.Outbound} stackId="day" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'members' && (
                <div className="voip-reports__charts">
                  {viewMode === 'table' && (
                  <div className="voip-reports__chart-card">
                    <div className="voip-reports__table-header">
                      <h3 className="voip-reports__chart-title">Member Breakdown</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="voip-reports__export-btn"
                          onClick={() => {
                            const cols = [
                              { key: 'name', header: 'Name' },
                              { key: 'extension', header: 'Ext' },
                              ...(memberGrouping !== 'total' ? [{ key: 'period', header: 'Period' }] : []),
                              { key: 'total', header: 'Total Calls' },
                              { key: 'avgPerDay', header: 'Avg. Calls/Day' },
                              { key: 'inbound', header: '# Inbound' },
                              { key: 'outbound', header: '# Outbound' },
                              { key: 'missed', header: '# Missed' },
                              { key: 'voicemail', header: '# Voicemail' },
                              { key: 'failed', header: '# Failed' },
                              { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` },
                              { key: 'avgHandleTime', header: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
                            ];
                            downloadCsv(cols, sortedMemberStats, csvFilename('Member Breakdown'));
                          }}
                        >
                          Export CSV
                        </button>
                        <div className="voip-reports__grouping-selector">
                          {[
                            { id: 'total', label: 'Total' },
                            { id: 'day', label: 'By Day' },
                            { id: 'week', label: 'By Week' },
                            { id: 'month', label: 'By Month' },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              className={`voip-reports__grouping-btn${memberGrouping === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
                              onClick={() => setMemberGrouping(opt.id)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="voip-reports__table-container">
                      <table className="voip-reports__table">
                        <thead>
                          <tr>
                            {[
                              { key: 'name', label: 'Name' },
                              { key: 'extension', label: 'Ext' },
                              ...(memberGrouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
                              { key: 'total', label: 'Total Calls' },
                              { key: 'avgPerDay', label: 'Avg. Calls/Day' },
                              { key: 'inbound', label: '# Inbound' },
                              { key: 'outbound', label: '# Outbound' },
                              { key: 'missed', label: '# Missed' },
                              { key: 'voicemail', label: '# Voicemail' },
                              { key: 'failed', label: '# Failed' },
                              { key: 'pctMissed', label: '% Missed' },
                              { key: 'avgHandleTime', label: 'Avg. Handle Time' },
                            ].map((col) => (
                              <th
                                key={col.key}
                                className="voip-reports__th--sortable"
                                onClick={() => handleSort(col.key)}
                              >
                                {col.label}
                                <span className={`voip-reports__sort-arrows${sortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                                  <span className={sortColumn === col.key && sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                                  <span className={sortColumn === col.key && !sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedMemberStats.map((m, idx) => (
                            <tr key={memberGrouping === 'total' ? m.extension : `${m.extension}-${m.period}-${idx}`}>
                              <td>{m.name}</td>
                              <td>{m.extension}</td>
                              {memberGrouping !== 'total' && <td>{m.period}</td>}
                              <td>{m.total}</td>
                              <td>{m.avgPerDay}</td>
                              <td>{m.inbound}</td>
                              <td>{m.outbound}</td>
                              <td>{m.missed}</td>
                              <td>{m.voicemail}</td>
                              <td>{m.failed}</td>
                              <td>{m.pctMissed}%</td>
                              <td>{formatHandleTime(m.avgHandleTime)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {viewMode === 'graphs' && (
                    <>

                      <div className="voip-reports__pie-row">
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Total Call Distribution Per Member</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'value', header: 'Total Calls' }], memberTotalsPieData, csvFilename('Total Call Distribution Per Member'))}>Export CSV</button>
                          </div>
                          {renderChart(300, (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={filteredMemberTotalsPieData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  innerRadius={50}
                                  isAnimationActive={false}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                  {filteredMemberTotalsPieData.map((entry) => {
                                    const origIndex = memberTotalsPieData.findIndex((d) => d.name === entry.name);
                                    return <Cell key={entry.name} fill={MEMBER_CHART_COLORS[origIndex % MEMBER_CHART_COLORS.length]} />;
                                  })}
                                </Pie>
                                <Tooltip />
                                <Legend payload={memberTotalsLegendPayload} onClick={toggleMember} />
                              </PieChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Missed Call % by Member</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` }], callsPerMember, csvFilename('Missed Call Pct by Member'))}>Export CSV</button>
                          </div>
                          {renderChart(Math.max(300, callsPerMember.length * 40), (
                            <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                              <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                                <Tooltip formatter={(v) => `${v}%`} />
                                <Bar dataKey="pctMissed" name="% Missed" fill={STATUS_COLORS.Missed} radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Total Calls Per Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'total', header: 'Total Calls' }], callsPerMember, csvFilename('Total Calls Per Member'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerMember.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                            <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Bar dataKey="total" name="Total Calls" radius={[0, 4, 4, 0]}>
                                {callsPerMember.map((entry, i) => (
                                  <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Daily Calls Per Member</h3>
                          <button
                            type="button"
                            className="voip-reports__export-btn"
                            onClick={() => {
                              const cols = [{ key: 'date', header: 'Date' }];
                              memberCallsPerDay.memberKeys.forEach((ext) => {
                                const member = callsPerMember.find((m) => m.extension === ext);
                                cols.push({ key: ext, header: member ? member.name : ext });
                              });
                              downloadCsv(cols, memberCallsPerDay.data, csvFilename('Daily Calls Per Member'));
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                        {renderChart(400, (
                          <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={memberCallsPerDay.data}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend onClick={toggleLineMember} />
                              {memberCallsPerDay.memberKeys.map((ext, i) => {
                                const member = callsPerMember.find((m) => m.extension === ext);
                                const label = member ? member.name : ext;
                                return (
                                  <Line
                                    key={ext}
                                    type="monotone"
                                    dataKey={ext}
                                    name={label}
                                    stroke={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]}
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                    hide={hiddenLineMembers.has(ext)}
                                  />
                                );
                              })}
                            </LineChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls Per Day by Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'avgPerDay', header: 'Avg Calls/Day' }], callsPerMember, csvFilename('Average Calls Per Day by Member'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerMember.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                            <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Bar dataKey="avgPerDay" name="Avg Calls/Day" radius={[0, 4, 4, 0]}>
                                {callsPerMember.map((entry, i) => (
                                  <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Inbound vs Outbound Per Member</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Name' }, { key: 'extension', header: 'Ext' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }], callsPerMember, csvFilename('Inbound vs Outbound Per Member'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerMember.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerMember.length * 40)}>
                            <BarChart data={callsPerMember} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="inbound" name="Inbound" fill={DIRECTION_COLORS.Inbound} radius={[0, 4, 4, 0]} />
                              <Bar dataKey="outbound" name="Outbound" fill={DIRECTION_COLORS.Outbound} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                    </>
                  )}

                </div>
              )}

              {activeTab === 'ringGroups' && (
                <div className="voip-reports__charts">
                  {viewMode === 'table' && (
                  <div className="voip-reports__chart-card">
                    <div className="voip-reports__table-header">
                      <h3 className="voip-reports__chart-title">Ring Group Breakdown</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="voip-reports__export-btn"
                          onClick={() => {
                            const cols = [
                              { key: 'name', header: 'Ring Group' },
                              { key: 'extension', header: 'Ext' },
                              ...(rgGrouping !== 'total' ? [{ key: 'period', header: 'Period' }] : []),
                              { key: 'total', header: 'Total Calls' },
                              { key: 'avgPerDay', header: 'Avg. Calls/Day' },
                              { key: 'answered', header: '# Answered' },
                              { key: 'missed', header: '# Missed' },
                              { key: 'voicemail', header: '# Voicemail' },
                              { key: 'failed', header: '# Failed' },
                              { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` },
                              { key: 'avgHandleTime', header: 'Avg. Handle Time', format: (r) => formatHandleTime(r.avgHandleTime) },
                            ];
                            downloadCsv(cols, sortedRingGroupStats, csvFilename('Ring Group Breakdown'));
                          }}
                        >
                          Export CSV
                        </button>
                        <div className="voip-reports__grouping-selector">
                          {[
                            { id: 'total', label: 'Total' },
                            { id: 'day', label: 'By Day' },
                            { id: 'week', label: 'By Week' },
                            { id: 'month', label: 'By Month' },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              className={`voip-reports__grouping-btn${rgGrouping === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
                              onClick={() => setRgGrouping(opt.id)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="voip-reports__table-container">
                      <table className="voip-reports__table">
                        <thead>
                          <tr>
                            {[
                              { key: 'name', label: 'Ring Group' },
                              { key: 'extension', label: 'Ext' },
                              ...(rgGrouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
                              { key: 'total', label: 'Total Calls' },
                              { key: 'avgPerDay', label: 'Avg. Calls/Day' },
                              { key: 'answered', label: '# Answered' },
                              { key: 'missed', label: '# Missed' },
                              { key: 'voicemail', label: '# Voicemail' },
                              { key: 'failed', label: '# Failed' },
                              { key: 'pctMissed', label: '% Missed' },
                              { key: 'avgHandleTime', label: 'Avg. Handle Time' },
                            ].map((col) => (
                              <th
                                key={col.key}
                                className="voip-reports__th--sortable"
                                onClick={() => handleRgSort(col.key)}
                              >
                                {col.label}
                                <span className={`voip-reports__sort-arrows${rgSortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                                  <span className={rgSortColumn === col.key && rgSortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                                  <span className={rgSortColumn === col.key && !rgSortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedRingGroupStats.map((g, idx) => (
                            <tr key={rgGrouping === 'total' ? g.name : `${g.name}-${g.period}-${idx}`}>
                              <td>{g.name}</td>
                              <td>{g.extension}</td>
                              {rgGrouping !== 'total' && <td>{g.period}</td>}
                              <td>{g.total}</td>
                              <td>{g.avgPerDay}</td>
                              <td>{g.answered}</td>
                              <td>{g.missed}</td>
                              <td>{g.voicemail}</td>
                              <td>{g.failed}</td>
                              <td>{g.pctMissed}%</td>
                              <td>{formatHandleTime(g.avgHandleTime)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}

                  {viewMode === 'graphs' && (
                    <>

                      <div className="voip-reports__pie-row">
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Total Call Distribution Per Ring Group</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Ring Group' }, { key: 'value', header: 'Total Calls' }], ringGroupTotalsPieData, csvFilename('Total Call Distribution Per Ring Group'))}>Export CSV</button>
                          </div>
                          {renderChart(300, (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={filteredRingGroupTotalsPieData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  innerRadius={50}
                                  isAnimationActive={false}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                  {filteredRingGroupTotalsPieData.map((entry) => {
                                    const origIndex = ringGroupTotalsPieData.findIndex((d) => d.name === entry.name);
                                    return <Cell key={entry.name} fill={MEMBER_CHART_COLORS[origIndex % MEMBER_CHART_COLORS.length]} />;
                                  })}
                                </Pie>
                                <Tooltip />
                                <Legend payload={ringGroupTotalsLegendPayload} onClick={toggleRingGroup} />
                              </PieChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                        <div className="voip-reports__chart-card voip-reports__chart-card--half">
                          <div className="voip-reports__chart-header">
                            <h3 className="voip-reports__chart-title">Missed Call % by Ring Group</h3>
                            <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Ring Group' }, { key: 'extension', header: 'Ext' }, { key: 'pctMissed', header: '% Missed', format: (r) => `${r.pctMissed}%` }], callsPerRingGroup, csvFilename('Missed Call Pct by Ring Group'))}>Export CSV</button>
                          </div>
                          {renderChart(Math.max(300, callsPerRingGroup.length * 40), (
                            <ResponsiveContainer width="100%" height={Math.max(300, callsPerRingGroup.length * 40)}>
                              <BarChart data={callsPerRingGroup} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                                <Tooltip formatter={(v) => `${v}%`} />
                                <Bar dataKey="pctMissed" name="% Missed" fill={STATUS_COLORS.Missed} radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ))}
                        </div>
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Total Calls Per Ring Group</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Ring Group' }, { key: 'extension', header: 'Ext' }, { key: 'total', header: 'Total Calls' }], callsPerRingGroup, csvFilename('Total Calls Per Ring Group'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerRingGroup.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerRingGroup.length * 40)}>
                            <BarChart data={callsPerRingGroup} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Bar dataKey="total" name="Total Calls" radius={[0, 4, 4, 0]}>
                                {callsPerRingGroup.map((entry, i) => (
                                  <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Daily Calls Per Ring Group</h3>
                          <button
                            type="button"
                            className="voip-reports__export-btn"
                            onClick={() => {
                              const cols = [{ key: 'date', header: 'Date' }];
                              ringGroupCallsPerDay.rgKeys.forEach((name) => {
                                cols.push({ key: name, header: name });
                              });
                              downloadCsv(cols, ringGroupCallsPerDay.data, csvFilename('Daily Calls Per Ring Group'));
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                        {renderChart(400, (
                          <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={ringGroupCallsPerDay.data}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Legend onClick={toggleLineRingGroup} />
                              {ringGroupCallsPerDay.rgKeys.map((name, i) => (
                                <Line
                                  key={name}
                                  type="monotone"
                                  dataKey={name}
                                  name={name}
                                  stroke={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]}
                                  strokeWidth={2}
                                  dot={false}
                                  connectNulls
                                  hide={hiddenLineRingGroups.has(name)}
                                />
                              ))}
                            </LineChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Average Calls Per Day by Ring Group</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Ring Group' }, { key: 'extension', header: 'Ext' }, { key: 'avgPerDay', header: 'Avg Calls/Day' }], callsPerRingGroup, csvFilename('Average Calls Per Day by Ring Group'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerRingGroup.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerRingGroup.length * 40)}>
                            <BarChart data={callsPerRingGroup} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Bar dataKey="avgPerDay" name="Avg Calls/Day" radius={[0, 4, 4, 0]}>
                                {callsPerRingGroup.map((entry, i) => (
                                  <Cell key={entry.name} fill={MEMBER_CHART_COLORS[i % MEMBER_CHART_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                      <div className="voip-reports__chart-card">
                        <div className="voip-reports__chart-header">
                          <h3 className="voip-reports__chart-title">Answered vs Missed Per Ring Group</h3>
                          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv([{ key: 'name', header: 'Ring Group' }, { key: 'extension', header: 'Ext' }, { key: 'answered', header: 'Answered' }, { key: 'missed', header: 'Missed' }], callsPerRingGroup, csvFilename('Answered vs Missed Per Ring Group'))}>Export CSV</button>
                        </div>
                        {renderChart(Math.max(300, callsPerRingGroup.length * 40), (
                          <ResponsiveContainer width="100%" height={Math.max(300, callsPerRingGroup.length * 40)}>
                            <BarChart data={callsPerRingGroup} layout="vertical" margin={{ left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="answered" name="Answered" fill={STATUS_COLORS.Answered} radius={[0, 4, 4, 0]} />
                              <Bar dataKey="missed" name="Missed" fill={STATUS_COLORS.Missed} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RhinoVOIPReports;
